import { useEffect, useRef, useState } from "react";
import { listStaff } from "@/lib/api";

/**
 * MentionInput — textarea with lightweight `@` autocomplete.
 *
 * Detects a trailing `@word` at the caret, shows a dropdown of matching staff
 * handles, and inserts `@handle ` on selection. No contentEditable, no rich
 * text — just a plain textarea plus a floating menu. Zero dependencies beyond
 * our staff API.
 *
 * The backend parses the same regex (`@handle`) server-side on save and emits
 * mention notifications, so this component is purely a UX helper.
 */
export function MentionInput({ value, onChange, className = "", ...props }) {
  const [staff, setStaff] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    listStaff().then(setStaff).catch(() => setStaff([]));
  }, []);

  const filtered = staff
    .filter(s => (s.handle || "").toLowerCase().startsWith(query.toLowerCase()))
    .slice(0, 5);

  const detectMention = (v, caret) => {
    const before = v.slice(0, caret);
    const m = /@([a-zA-Z0-9_\-\.]{0,30})$/.exec(before);
    if (m) {
      setQuery(m[1]);
      setSelectedIdx(0);
      setShowMenu(true);
    } else {
      setShowMenu(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    detectMention(v, e.target.selectionStart);
  };

  const insertMention = (handle) => {
    const el = ref.current;
    if (!el) return;
    const caret = el.selectionStart;
    const beforeRaw = value.slice(0, caret);
    const before = beforeRaw.replace(/@([a-zA-Z0-9_\-\.]{0,30})$/, `@${handle} `);
    const after = value.slice(caret);
    const newVal = before + after;
    onChange(newVal);
    setShowMenu(false);
    setTimeout(() => {
      el.focus();
      const newCaret = before.length;
      el.setSelectionRange(newCaret, newCaret);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!showMenu || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(i => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(i => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertMention(filtered[selectedIdx].handle);
    } else if (e.key === "Escape") {
      setShowMenu(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setShowMenu(false), 150)}
        data-testid="mention-input"
        className={className}
        {...props}
      />
      {showMenu && filtered.length > 0 && (
        <div
          className="absolute z-50 left-2 bottom-full mb-1 bg-white border border-stone-300 shadow-lg w-64 max-h-52 overflow-y-auto"
          data-testid="mention-menu"
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-stone-500 bg-stone-50 border-b border-stone-200">
            Mention a teammate
          </div>
          {filtered.map((s, i) => (
            <button
              key={s.email}
              type="button"
              onMouseDown={(e) => e.preventDefault()}  // prevent blur before click
              onClick={() => insertMention(s.handle)}
              data-testid={`mention-option-${s.handle}`}
              className={`w-full text-left px-3 py-2 text-sm ${i === selectedIdx ? "bg-ccc-purple/10 text-ccc-plum" : "hover:bg-stone-50"}`}
            >
              <div className="font-medium">@{s.handle}</div>
              <div className="text-xs text-stone-500 truncate">{s.name || s.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
